import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Form, Button, Spin, message, Card, Typography, Checkbox, Alert } from 'antd'; // Added Alert
import medicalHistoryDiagnoses from '../utils/medicalHistoryDiagnoses';

const { Title } = Typography;

// Helper to create Checkbox.Group options
const createCheckboxOptions = (diagnoses) => {
    return diagnoses.map(diagnosis => ({ label: diagnosis, value: diagnosis }));
};

// Helper to render system titles
const formatSystemName = (systemKey) => {
    if (!systemKey) return '';

    // --- Add specific Vietnamese names ---
    if (systemKey === 'neurological') return 'THẦN KINH';
    if (systemKey === 'cardiovascular') return 'TUẦN HOÀN';
    if (systemKey === 'respiratory') return 'HÔ HẤP';
    if (systemKey === 'renal') return 'THẬN NIỆU - LỌC MÁU';
    if (systemKey === 'metabolic') return 'CHUYỂN HÓA - TIÊU HÓA';
    if (systemKey === 'musculoskeletal') return 'CƠ XƯƠNG KHỚP';
    if (systemKey === 'immune') return 'MIỄN DỊCH - DỊ ỨNG - VIÊM';
    if (systemKey === 'womensHealth') return 'NỮ KHOA';
    if (systemKey === 'mensHealth') return 'NAM KHOA';
    if (systemKey === 'cancer') return 'UNG THƯ (đã có hoặc đang điều trị)';
    // --- End specific names ---

    // Exclude internal fields from display title logic (though less relevant in the form itself)
    if (['lastUpdatedAt', 'lastUpdatedBy', '_id'].includes(systemKey)) return null;

    // Default formatting for other keys (e.g., 'other')
    return systemKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
};

const StructuredMedicalHistoryForm = ({ patientId, onSaveSuccess, onCancel }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [fetchError, setFetchError] = useState(null);

    const fetchHistory = useCallback(async () => {
        if (!patientId) return;
        setFetching(true);
        setFetchError(null);
        try {
            // Fetching the whole patient object here to get the current history
            const response = await axios.get(`/patients/${patientId}`);
            const patientData = response.data.data.patient;
            const currentHistory = patientData.structuredMedicalHistory || {};

            console.log("Fetched structured medical history:", currentHistory);

            // Validate that we have the expected structure
            if (currentHistory && typeof currentHistory === 'object') {
                console.log("✅ Valid structured medical history data found");
            } else {
                console.warn("⚠️ No structured medical history data found, using empty structure");
            }

            // Fix: Set the form values with the correct nested structure
            // The form expects { structuredMedicalHistory: { systemKey: [values] } }
            form.setFieldsValue({
                structuredMedicalHistory: {
                    neurological: currentHistory.neurological || [],
                    cardiovascular: currentHistory.cardiovascular || [],
                    respiratory: currentHistory.respiratory || [],
                    metabolic: currentHistory.metabolic || [],
                    renal: currentHistory.renal || [],
                    musculoskeletal: currentHistory.musculoskeletal || [],
                    immune: currentHistory.immune || [],
                    womensHealth: currentHistory.womensHealth || [],
                    mensHealth: currentHistory.mensHealth || [],
                    cancer: currentHistory.cancer || [],
                    other: currentHistory.other || []
                }
            });

        } catch (err) {
            console.error("Error fetching structured medical history:", err);
            setFetchError(err.response?.data?.message || "Failed to load current history.");
            // Reset form with empty arrays for each system
            form.setFieldsValue({
                structuredMedicalHistory: {
                    neurological: [],
                    cardiovascular: [],
                    respiratory: [],
                    metabolic: [],
                    renal: [],
                    musculoskeletal: [],
                    immune: [],
                    womensHealth: [],
                    mensHealth: [],
                    cancer: [],
                    other: []
                }
            });
        } finally {
            setFetching(false);
        }
    }, [patientId, form]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    const handleFinish = async (values) => {
        if (!patientId) {
            message.error("Patient ID is missing.");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                 // Send only the structuredMedicalHistory field for update via PATCH
                structuredMedicalHistory: values.structuredMedicalHistory || {}
            };

            await axios.patch(`/patients/${patientId}`, payload);

            message.success('Medical history checklist updated successfully!');
            if (onSaveSuccess) {
                onSaveSuccess(); // Callback for parent (e.g., close modal, refetch patient data)
            }
        } catch (err) {
            console.error("Error saving structured medical history:", err);
            message.error(err.response?.data?.message || 'Failed to save history checklist.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Spin spinning={loading || fetching}>
             {fetchError && <Alert message={fetchError} type="error" showIcon style={{ marginBottom: 16 }} />}
            <Form form={form} layout="vertical" onFinish={handleFinish}>
                <Card size="small" title="Structured Medical History Checklist">
                    {Object.entries(medicalHistoryDiagnoses)
                        .filter(([key, value]) => key !== 'other' || value.length > 0)
                        .map(([systemKey, diagnoses]) => {
                             const systemName = formatSystemName(systemKey);
                             return systemName ? ( // Render only if systemName is valid
                                <Form.Item
                                    key={systemKey}
                                    name={['structuredMedicalHistory', systemKey]}
                                    label={<Title level={5} style={{ marginTop: '10px', marginBottom: '5px' }}>{systemName}</Title>}
                                    style={{ marginBottom: 10 }}
                                >
                                    <Checkbox.Group options={createCheckboxOptions(diagnoses)} />
                                </Form.Item>
                             ) : null;
                        })}
                </Card>

                <Form.Item style={{ marginTop: 24, textAlign: 'right' }}>
                    <Button style={{ marginRight: 8 }} onClick={onCancel} disabled={loading}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        Save Checklist
                    </Button>
                </Form.Item>
            </Form>
        </Spin>
    );
};

export default StructuredMedicalHistoryForm;
